import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ImageElement, MultiPageImageModel, MultiPageVideoModel } from '@bynder-studio/render-core';
import { Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { setShowAssetSelection } from 'packages/store/creativeEditor/creativeEditor.actions';
import { showAssetSelectionSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import {
    getElementPropertiesFromSource,
    getResetContentTransformOnSourceChange,
    syncLinkedContentPropertyTransforms,
} from 'packages/common/editor/helpers';
import BrowseButton from '../../../FormComponents/BrowseButton';
import { ContentType } from '../../../FormComponents/BrowseButton/types';
import useEditor from 'packages/hooks/useEditor';

type Props = {
    creativeModel: MultiPageVideoModel | MultiPageImageModel;
    selectedElement: ImageElement;
    disabled?: boolean;
};

const BrowseImage = ({ creativeModel, selectedElement, disabled }: Props) => {
    const { translate } = useTranslate();
    const { addImageGenPendingAssetId } = useEditor();
    const showAssetSelection: boolean = useSelector(showAssetSelectionSelector);
    const { locked, duration, src, fileName, id: elementId } = selectedElement;

    const handleSelectSource = useCallback(
        (selectedSource) => {
            const params = getElementPropertiesFromSource(selectedElement, 'IMAGE', selectedSource);

            const resetDefault = selectedElement.srcId !== selectedSource.id;

            if (resetDefault) {
                const resetContentTransform = getResetContentTransformOnSourceChange(
                    selectedElement,
                    params.naturalDimension,
                );

                if (resetContentTransform) {
                    params.contentTransform = resetContentTransform;
                }
            }

            creativeModel.updateElement(elementId, params);

            syncLinkedContentPropertyTransforms({
                creativeModel,
                sourceElement: selectedElement,
                contentTransform: params.contentTransform,
                naturalDimension: params.naturalDimension,
            });
        },
        [creativeModel, elementId, selectedElement],
    );

    return (
        <Form.Group>
            {src && <Form.Label>{translate('editor.sidebar.shots.image.tabs.content')}</Form.Label>}
            <BrowseButton
                type={ContentType.IMAGE}
                frameRate={0}
                fileName={fileName}
                selectedObject={{ element: selectedElement, duration }}
                disabled={locked || disabled}
                handleSelectSource={handleSelectSource}
                show={showAssetSelection}
                setShowAssetSelection={setShowAssetSelection}
                addImageGenPendingAssetId={addImageGenPendingAssetId}
                mediaSrc={src}
                testId="design_scene_image_asset"
                resizableRange
                isEditorPage
            />
        </Form.Group>
    );
};

export default BrowseImage;
